import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../store/authStore';
import { Card, Button, Input } from '../components';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface EmployerProfileForm {
  bedrijfsnaam: string;
  kvk_nummer?: string;
  beschrijving?: string;
  website?: string;
  adres?: string;
  postcode?: string;
  plaats?: string;
  contactpersoon?: string;
  contactpersoon_functie?: string;
  aantal_medewerkers?: number;
  bedrijfsgrootte?: 'klein' | 'middel' | 'groot';
  factuur_email?: string;
}

export const EmployerProfilePage: React.FC = () => {
  const { t } = useTranslation('employer');
  useCockpitTitle(t('profile.title', 'Bedrijfsprofiel'), t('profile.subtitle', 'Beheer uw bedrijfsgegevens en zorg ervoor dat professionals u kunnen vinden'));
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<EmployerProfileForm>();
  const kvkNummer = watch('kvk_nummer');
  const [isLoadingKvk, setIsLoadingKvk] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({});
  const [notifSaving, setNotifSaving] = useState(false);

  // Fetch KVK data when KvK number is entered
  const fetchKvkData = useCallback(async (kvkNumber: string) => {
    if (!kvkNumber || kvkNumber.length !== 8 || !/^\d{8}$/.test(kvkNumber)) {
      return;
    }

    setIsLoadingKvk(true);
    try {
      const response = await api.get('/employer/kvk-data', {
        params: { kvkNummer: kvkNumber }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;

        // Set all fields from KVK data
        if (data.bedrijfsnaam) {
          setValue('bedrijfsnaam', data.bedrijfsnaam);
        }
        if (data.adres) {
          setValue('adres', data.adres);
        }
        if (data.postcode) {
          setValue('postcode', data.postcode);
        }
        if (data.plaats) {
          setValue('plaats', data.plaats);
        }
        if (data.website) {
          setValue('website', data.website);
        }

        toast.success(t('profile.kvkAutoFilled', 'Bedrijfsgegevens automatisch ingevuld vanuit KVK'));
      } else {
        toast.error(response.data.message || t('profile.kvkNotFound', 'Geen bedrijfsgegevens gevonden voor dit KvK nummer'));
      }
    } catch (error: any) {
      console.error('Error fetching KVK data:', error);
      const errorMessage = error?.response?.data?.message || t('profile.kvkFetchError', 'Kon bedrijfsgegevens niet ophalen van KVK API');
      toast.error(errorMessage);
    } finally {
      setIsLoadingKvk(false);
    }
  }, [setValue, t]);

  // Watch for KvK number changes
  useEffect(() => {
    if (kvkNummer && kvkNummer.length === 8 && /^\d{8}$/.test(kvkNummer)) {
      // Debounce the API call
      const timer = setTimeout(() => {
        fetchKvkData(kvkNummer);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [kvkNummer, fetchKvkData]);

  // Load existing profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!user || user.role !== 'opdrachtgever') return;

      try {
        const response = await api.get('/employer/profile');
        if (response.data.success && response.data.data) {
          const profileData = response.data.data;
          setProfile(profileData);
          setLogoUrl(profileData.logo_url || null);
          reset({
            bedrijfsnaam: profileData.bedrijfsnaam || '',
            kvk_nummer: profileData.kvk_nummer || '',
            beschrijving: profileData.beschrijving || '',
            website: profileData.website || '',
            adres: profileData.adres || '',
            postcode: profileData.postcode || '',
            plaats: profileData.plaats || '',
            contactpersoon: profileData.contactpersoon || '',
            contactpersoon_functie: profileData.contactpersoon_functie || '',
            aantal_medewerkers: profileData.aantal_medewerkers || undefined,
            bedrijfsgrootte: profileData.bedrijfsgrootte || undefined,
            factuur_email: profileData.factuur_email || user?.email || '',
          });
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user, reset]);

  useEffect(() => {
    const NOTIF_DEFAULTS = {
      email_new_message: true, email_connection_request: true, email_dossier_update: true,
      email_invoice: true, email_community: false, email_vacancy: true,
      inapp_new_message: true, inapp_connection_request: true, inapp_dossier_update: true,
      inapp_invoice: true, inapp_community: true, inapp_vacancy: true,
    };
    api.get('/users/notification-preferences')
      .then(res => setNotifPrefs({ ...NOTIF_DEFAULTS, ...(res.data.data || {}) }))
      .catch(() => setNotifPrefs(NOTIF_DEFAULTS));
  }, []);

  const toggleNotif = async (key: string) => {
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    setNotifSaving(true);
    try {
      await api.patch('/users/notification-preferences', updated);
      toast.success('Instellingen opgeslagen');
    } catch {
      toast.error('Fout bij opslaan');
    } finally {
      setNotifSaving(false);
    }
  };

  if (!user || user.role !== 'opdrachtgever') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <p className="text-sm text-gray-700">{t('profile.employerOnly', 'Alleen opdrachtgevers kunnen hun bedrijfsprofiel beheren.')}</p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">{t('profile.loading', 'Profiel laden...')}</p>
          </div>
        </Card>
      </div>
    );
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('company_photo', file);

    setUploadingPhoto(true);
    try {
      const response = await api.post('/employer/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setLogoUrl(response.data.data.logo_url);
        toast.success(t('profile.photoUploaded', 'Bedrijfsfoto succesvol geüpload'));
      }
    } catch {
      toast.error(t('profile.photoUploadError', 'Fout bij uploaden van bedrijfsfoto'));
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: EmployerProfileForm) => {
    setLoading(true);
    try {
      const response = profile
        ? await api.put('/employer/profile', data)
        : await api.post('/employer/profile', data);

      if (response.data.success) {
        setProfile(response.data.data);
        toast.success(profile ? t('profile.updated', 'Profiel succesvol bijgewerkt!') : t('profile.created', 'Profiel succesvol aangemaakt!'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('profile.saveError', 'Er is een fout opgetreden bij het opslaan van het profiel.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Form */}
        <div className="lg:col-span-2">
          <Card>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.companyName', 'Bedrijfsnaam')} *
                  </label>
                  <Input
                    placeholder={t('profile.companyNamePlaceholder', 'Uw bedrijfsnaam')}
                    error={errors.bedrijfsnaam?.message}
                    {...register('bedrijfsnaam', {
                      required: t('profile.companyNameRequired', 'Bedrijfsnaam is verplicht'),
                      minLength: { value: 2, message: t('profile.minChars', 'Minimaal 2 karakters') }
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.kvkNumber', 'KvK nummer')}
                  </label>
                  <div className="relative">
                    <Input
                      placeholder="12345678"
                      error={errors.kvk_nummer?.message}
                      {...register('kvk_nummer', {
                        pattern: {
                          value: /^[0-9]{8}$/,
                          message: t('profile.kvkInvalid', 'KvK nummer moet 8 cijfers bevatten')
                        }
                      })}
                    />
                    {isLoadingKvk && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {t('profile.kvkHint', 'Vul uw KvK nummer in om automatisch bedrijfsgegevens op te halen')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.website', 'Website')}
                  </label>
                  <Input
                    type="url"
                    placeholder="https://www.bedrijf.nl"
                    error={errors.website?.message}
                    {...register('website', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: t('profile.websiteInvalid', 'Voer een geldige URL in (met http:// of https://)')
                      }
                    })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.companyPhotoLabel', 'Bedrijfsfoto / logo (optioneel)')}
                </label>
                <div className="flex items-center gap-4">
                  {logoUrl && (
                    <img
                      src={`/uploads/${logoUrl}`}
                      alt={t('profile.companyLogo', 'Bedrijfslogo')}
                      className="w-20 h-20 object-contain rounded-lg border border-gray-200 bg-gray-50"
                    />
                  )}
                  <div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      loading={uploadingPhoto}
                      onClick={() => photoInputRef.current?.click()}
                    >
                      {logoUrl ? t('profile.changePhoto', 'Foto wijzigen') : t('profile.uploadPhoto', 'Foto uploaden')}
                    </Button>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('profile.photoHint', 'JPG, PNG of GIF, max 5MB. Wordt getoond aan professionals.')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.employeeCount', 'Aantal medewerkers (optioneel)')}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    placeholder={t('profile.employeeCountPlaceholder', 'Bijvoorbeeld: 50')}
                    error={errors.aantal_medewerkers?.message}
                    {...register('aantal_medewerkers', {
                      valueAsNumber: true
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.companySize', 'Bedrijfsgrootte (optioneel)')}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...register('bedrijfsgrootte')}
                  >
                    <option value="">{t('profile.selectSize', 'Selecteer grootte')}</option>
                    <option value="klein">{t('profile.sizeSmall', 'Klein (1-50 medewerkers)')}</option>
                    <option value="middel">{t('profile.sizeMedium', 'Middel (51-250 medewerkers)')}</option>
                    <option value="groot">{t('profile.sizeLarge', 'Groot (250+ medewerkers)')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.description', 'Bedrijfsbeschrijving')}
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px]"
                  placeholder={t('profile.descriptionPlaceholder', 'Vertel over uw bedrijf, uw visie, missie, en wat u doet. Dit helpt professionals om een beter beeld te krijgen van uw organisatie.')}
                  {...register('beschrijving', {
                    maxLength: { value: 1000, message: t('profile.maxChars', 'Maximaal 1000 karakters') }
                  })}
                />
                {errors.beschrijving && (
                  <p className="mt-1 text-sm text-red-600">{errors.beschrijving.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t('profile.descriptionHint', 'Let op: beschrijf hier ALLEEN uw bedrijf en visie, geen specifieke opdrachten')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.address', 'Adres')}
                  </label>
                  <Input
                    placeholder={t('profile.addressPlaceholder', 'Straatnaam 123')}
                    error={errors.adres?.message}
                    {...register('adres')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.postalCode', 'Postcode')}
                  </label>
                  <Input
                    placeholder="1234 AB"
                    error={errors.postcode?.message}
                    {...register('postcode', {
                      pattern: {
                        value: /^[1-9][0-9]{3} ?[A-Za-z]{2}$/,
                        message: t('profile.postalCodeInvalid', 'Voer een geldige postcode in (1234 AB)')
                      }
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.city', 'Plaats')}
                  </label>
                  <Input
                    placeholder="Amsterdam"
                    error={errors.plaats?.message}
                    {...register('plaats')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.contactPerson', 'Contactpersoon')}
                  </label>
                  <Input
                    placeholder="Jan Jansen"
                    error={errors.contactpersoon?.message}
                    {...register('contactpersoon')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.contactFunction', 'Functie contactpersoon')}
                  </label>
                  <Input
                    placeholder="HR Manager"
                    error={errors.contactpersoon_functie?.message}
                    {...register('contactpersoon_functie')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.invoiceEmail', 'Email voor facturen')} *
                </label>
                <Input
                  type="email"
                  placeholder="factuur@voorbeeld.nl"
                  error={errors.factuur_email?.message}
                  {...register('factuur_email', {
                    required: t('profile.invoiceEmailRequired', 'Email voor facturen is verplicht'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('profile.emailInvalid', 'Ongeldig email adres')
                    }
                  })}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('profile.invoiceEmailHint', 'Dit emailadres wordt automatisch ingevuld bij het aanmaken van nieuwe opdrachten')}
                </p>
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  {t('common:actions.cancel', 'Annuleren')}
                </Button>
                <Button
                  type="submit"
                  loading={loading}
                >
                  {profile ? t('profile.updateProfile', 'Profiel bijwerken') : t('profile.createProfile', 'Profiel aanmaken')}
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Profile Summary */}
        <div className="lg:col-span-1">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.summary', 'Profiel overzicht')}</h3>

            {profile ? (
              <div className="space-y-4">
                {logoUrl && (
                  <img
                    src={`/uploads/${logoUrl}`}
                    alt={t('profile.companyLogo', 'Bedrijfslogo')}
                    className="w-24 h-24 object-contain rounded-lg border border-gray-200 bg-gray-50"
                  />
                )}
                <div>
                  <h4 className="font-medium text-gray-900">{profile.bedrijfsnaam}</h4>
                  {profile.kvk_nummer && (
                    <p className="text-sm text-gray-600">{t('profile.kvkLabel', 'KvK')}: {profile.kvk_nummer}</p>
                  )}
                </div>

                {profile.beschrijving && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">{t('profile.aboutUs', 'Over ons')}</h5>
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {profile.beschrijving}
                    </p>
                  </div>
                )}

                {(profile.adres || profile.postcode || profile.plaats) && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">{t('profile.address', 'Adres')}</h5>
                    <p className="text-sm text-gray-600">
                      {[profile.adres, profile.postcode, profile.plaats]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  </div>
                )}

                {profile.contactpersoon && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">{t('profile.contact', 'Contact')}</h5>
                    <p className="text-sm text-gray-600">
                      {profile.contactpersoon}
                      {profile.contactpersoon_functie && (
                        <span className="block text-gray-500">
                          {profile.contactpersoon_functie}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {profile.website && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">{t('profile.website', 'Website')}</h5>
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {profile.website}
                    </a>
                  </div>
                )}

                {profile.gemiddelde_beoordeling > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">{t('profile.rating', 'Beoordeling')}</h5>
                    <div className="flex items-center">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${i < Math.floor(profile.gemiddelde_beoordeling) ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="ml-2 text-sm text-gray-600">
                        {profile.gemiddelde_beoordeling.toFixed(1)} ({profile.aantal_beoordelingen} {t('profile.reviews', 'reviews')})
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                {t('profile.completePlaceholder', 'Vul uw bedrijfsgegevens in om uw profiel te voltooien.')}
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Notification preferences */}
      <div className="mt-8">
        <Card>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Notificatie-instellingen</h3>
          <p className="text-sm text-gray-500 mb-4">Stel in via welke kanalen u meldingen wilt ontvangen.</p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-3 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 px-6 py-3 border-b border-gray-200">
              <span>Notificatie</span>
              <span className="text-center">E-mail</span>
              <span className="text-center">In-app</span>
            </div>
            <div className="divide-y divide-gray-100">
              {([
                { key: 'new_message', label: 'Nieuw bericht', desc: 'Iemand stuurt u een bericht' },
                { key: 'connection_request', label: 'Verbindingsverzoek', desc: 'Een professional heeft gereageerd' },
                { key: 'dossier_update', label: 'Dossier update', desc: 'Wijziging in een klantdossier' },
                { key: 'invoice', label: 'Factuur', desc: 'Nieuwe of betaalde factuur' },
                { key: 'vacancy', label: 'Nieuw aanbod', desc: 'Geschikte professionals beschikbaar' },
                { key: 'community', label: 'Community', desc: 'Activiteit in de community' },
              ] as const).map(({ key, label, desc }) => (
                <div key={key} className="grid grid-cols-3 px-6 py-4 items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                  {(['email', 'inapp'] as const).map(channel => {
                    const prefKey = `${channel}_${key}`;
                    return (
                      <div key={channel} className="flex justify-center">
                        <button
                          onClick={() => toggleNotif(prefKey)}
                          disabled={notifSaving}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${notifPrefs[prefKey] ? 'bg-blue-600' : 'bg-gray-200'}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${notifPrefs[prefKey] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
